import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ChartProvider from '../../../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../../../charts/echarts/ColorProvider'
import Tooltip from '../../../../../charts/echarts/Tooltip'
import AxisX from '../../../../../charts/echarts/AxisX'
import AxisY from '../../../../../charts/echarts/AxisY'
import ComponentScatter from '../../../../../charts/echarts/ComponentScatter'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { buildScatterTooltipFormatter } from './scatterTooltip'

// ECharts scatter wrapper for the compare tool. Two value axes (each
// independently formatted via the axis's own `valueFormat` -- usually
// different, e.g. X currency, Y return %), one series with N points.
// `symbolSize` is fixed in v1; the prop accepts a function for the
// future bubble-size enhancement (size = third measure).
//
// `points`: Array<{ x: number, y: number, name, groupId }>. Rows with
// null x or null y are filtered upstream in ScatterTool -- ECharts
// can't position a point at NaN, so dropping them is the only sane
// behavior. The detail table still shows the dropped rows so the user
// can see why a name they expected isn't on the chart.

export default function ScatterChart ({
  points,
  axisXLabel,
  axisYLabel,
  axisXFormat,
  axisYFormat,
  symbolSize = 8,
  height = 340
}) {
  const { formatter: fmt } = useFormattingContext()
  const tooltipFormatter = useMemo(
    () => buildScatterTooltipFormatter(fmt, { axisXLabel, axisYLabel, axisXFormat, axisYFormat }),
    [fmt, axisXLabel, axisYLabel, axisXFormat, axisYFormat]
  )

  // ECharts scatter `data` accepts entries either as `[x, y]` tuples or
  // as objects with `value: [x, y]` + arbitrary extra fields that
  // tooltips can read via `params.data`. The object form lets us thread
  // `name` and `groupId` through to the formatter without smuggling
  // them into the array.
  const data = useMemo(() => points.map(p => ({
    value: [p.x, p.y],
    name: p.name,
    groupId: p.groupId
  })), [points])

  return (
    <ChartColorProvider theme='default'>
      <ChartProvider height={height} loadingTemplate='axis' emptyTemplate='axis'>
        <Tooltip trigger='item' formatter={tooltipFormatter} />
        <AxisX
          type='value'
          format={axisXFormat || null}
          name={axisXLabel}
          nameLocation='middle'
          nameGap={28}
          nameTextStyle={{ fontSize: 11, color: 'var(--color-neutral-700, #555)' }}
          axisLabel={{ fontSize: 10, hideOverlap: true }}
          splitLine={{ lineStyle: { color: 'var(--opal-border-default, #eee)' } }}
        />
        <AxisY
          type='value'
          format={axisYFormat || null}
          name={axisYLabel}
          nameLocation='middle'
          nameGap={48}
          nameTextStyle={{ fontSize: 11, color: 'var(--color-neutral-700, #555)' }}
          axisLabel={{ fontSize: 10 }}
          splitLine={{ lineStyle: { color: 'var(--opal-border-default, #eee)' } }}
        />
        <ComponentScatter
          seriesId='scatter'
          data={data}
          symbolSize={symbolSize}
          // Cursor stays default; we're not wiring click-on-point
          // drilling in v1.
        />
      </ChartProvider>
    </ChartColorProvider>
  )
}

ScatterChart.propTypes = {
  points: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    name: PropTypes.string,
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })).isRequired,
  axisXLabel: PropTypes.string,
  axisYLabel: PropTypes.string,
  axisXFormat: PropTypes.string,
  axisYFormat: PropTypes.string,
  symbolSize: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  height: PropTypes.number
}
